import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Share2, Sparkles, Search, MessageCircle, FolderOpen, Lock, Check } from 'lucide-react';
import { Button } from '../components/ui/button';

// HowItWorksPage - concrete, step-by-step content page that answers
// the GEO-critical question "how does BrainSync actually work?" with
// enough specificity that LLMs can pull individual sentences as
// citations. Heavy use of <ol>, <dl>, and HowTo schema gives both
// Google rich-snippets and LLMs structured anchors.
export default function HowItWorksPage() {
    useEffect(() => {
        document.title = 'How BrainSync Works - Save Anything. Transcribe. Summarise. Find it Faster.';
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute('content', 'Share any link to BrainSync. Whisper transcribes audio. GPT summarizes. Smart tags categorize. You search and chat with everything. Here is exactly how it works.');
        }
        if (typeof window !== 'undefined' && window.__brainsyncDismissSplash) {
            window.__brainsyncDismissSplash();
        }
    }, []);

    return (
        <main className="min-h-screen bg-[#FDFCF8] text-[#1F2D20]" data-testid="how-it-works-page">
            {/* HowTo schema - Google's structured snippet format for
                step-by-step content. Also gives LLMs an unambiguous
                machine-readable description of the workflow. */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'HowTo',
                    '@id': 'https://brainsync.pro/how-it-works#howto',
                    'name': 'How to use BrainSync to save and find anything',
                    'description': 'Step-by-step: share a link from any app to BrainSync, let AI transcribe and summarize, then find it later via search or AI chat.',
                    'totalTime': 'PT30S',
                    'tool': [
                        { '@type': 'HowToTool', 'name': 'BrainSync' },
                        { '@type': 'HowToTool', 'name': 'Your phone or browser' },
                    ],
                    'step': [
                        {
                            '@type': 'HowToStep',
                            'position': 1,
                            'name': 'Share a link',
                            'text': 'From any app (TikTok, Instagram, YouTube, Chrome, Safari, WhatsApp), tap the share button and pick BrainSync.',
                            'url': 'https://brainsync.pro/how-it-works#step-share',
                        },
                        {
                            '@type': 'HowToStep',
                            'position': 2,
                            'name': 'AI processes the content',
                            'text': 'BrainSync transcribes any audio/video with OpenAI Whisper, summarizes the text with GPT, extracts recipes if applicable, and auto-tags into your collections.',
                            'url': 'https://brainsync.pro/how-it-works#step-process',
                        },
                        {
                            '@type': 'HowToStep',
                            'position': 3,
                            'name': 'Find it later',
                            'text': 'Search across everything semantically, browse by smart collection, or ask BrainSync\'s AI chat about anything you have saved.',
                            'url': 'https://brainsync.pro/how-it-works#step-find',
                        },
                    ],
                }) }}
            />

            <header className="px-6 pt-10 pb-8 sm:pt-16 max-w-3xl mx-auto">
                <Link to="/" className="text-xs font-semibold uppercase tracking-wider text-emerald-700 hover:text-emerald-800" data-testid="how-it-works-back-link">
                    ← BrainSync
                </Link>
                <h1 className="mt-4 font-heading text-3xl sm:text-5xl font-bold tracking-tight leading-[1.1]">
                    How BrainSync works
                </h1>
                <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
                    Three steps. Thirty seconds. From "I want to save this" to "I want to find that thing I saved" - without you ever opening a note-taking app.
                </p>
            </header>

            <section className="px-6 max-w-3xl mx-auto space-y-12 pb-20">
                {/* Step 1 */}
                <div id="step-share">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center font-heading text-2xl font-bold text-emerald-800">1</div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">Share a link from any app</h2>
                            <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                                On your phone, tap the share button inside any app - TikTok, Instagram Reels, YouTube, X, Chrome, Safari, WhatsApp forwards - and pick <strong>BrainSync</strong> from the share sheet. On desktop, paste the URL into the input or use the Chrome extension. There's no copy-paste, no separate URL field, no friction.
                            </p>
                            <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                                BrainSync accepts almost any URL: blog posts and news articles, full YouTube videos and Shorts, TikTok and Instagram Reels, podcast episodes, recipe sites, X threads, Google Maps places, even images and screenshots.
                            </p>
                            <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800" data-testid="how-it-works-share-cta">
                                Try it now <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Step 2 */}
                <div id="step-process">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center font-heading text-2xl font-bold text-emerald-800">2</div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">AI does the reading for you</h2>
                            <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                                Within seconds of saving, BrainSync's pipeline runs four things in parallel:
                            </p>
                            <dl className="mt-4 space-y-3 text-[15px]">
                                <div className="flex gap-3">
                                    <Check className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                    <div>
                                        <dt className="font-semibold text-zinc-800 inline">Transcription. </dt>
                                        <dd className="text-zinc-600 inline">Audio from videos and podcasts is transcribed by OpenAI Whisper into a full, searchable transcript.</dd>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Check className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                    <div>
                                        <dt className="font-semibold text-zinc-800 inline">Summarization. </dt>
                                        <dd className="text-zinc-600 inline">GPT generates a concise summary in your preferred language (bilingual support built in).</dd>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Check className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                    <div>
                                        <dt className="font-semibold text-zinc-800 inline">Structured extraction. </dt>
                                        <dd className="text-zinc-600 inline">Recipes get parsed into ingredients + steps + cook time. Maps places get venue metadata. X threads get reconstructed.</dd>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    <Check className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" strokeWidth={3} />
                                    <div>
                                        <dt className="font-semibold text-zinc-800 inline">Auto-categorization. </dt>
                                        <dd className="text-zinc-600 inline">Smart tags route the bookmark into one of your collections - or suggest a new one if it doesn't fit.</dd>
                                    </div>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>

                {/* Step 3 */}
                <div id="step-find">
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center font-heading text-2xl font-bold text-emerald-800">3</div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">Find it again - three ways</h2>
                            <p className="mt-3 text-[15px] text-zinc-600 leading-relaxed">
                                Saving is only useful if you can retrieve. BrainSync gives you three retrieval paths so you'll actually find the thing you saved:
                            </p>
                            <div className="mt-5 grid gap-4 sm:grid-cols-3">
                                <div className="rounded-2xl border border-zinc-200 p-4 bg-white">
                                    <Search className="w-5 h-5 text-emerald-700" />
                                    <div className="mt-2 font-semibold text-sm">Semantic search</div>
                                    <p className="mt-1 text-xs text-zinc-500 leading-relaxed">Search by meaning, not just keywords. "Mushroom dishes" finds your risotto.</p>
                                </div>
                                <div className="rounded-2xl border border-zinc-200 p-4 bg-white">
                                    <FolderOpen className="w-5 h-5 text-emerald-700" />
                                    <div className="mt-2 font-semibold text-sm">Smart collections</div>
                                    <p className="mt-1 text-xs text-zinc-500 leading-relaxed">Auto-organized by topic. Browse Recipes, Articles, Videos, anything.</p>
                                </div>
                                <div className="rounded-2xl border border-zinc-200 p-4 bg-white">
                                    <MessageCircle className="w-5 h-5 text-emerald-700" />
                                    <div className="mt-2 font-semibold text-sm">AI chat</div>
                                    <p className="mt-1 text-xs text-zinc-500 leading-relaxed">"Which podcasts did I save about productivity?" Get answers from your own library.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Privacy reassurance - also a GEO answer to "is my data private" */}
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 flex items-start gap-4">
                    <Lock className="w-6 h-6 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight">Your library is private</h3>
                        <p className="mt-1.5 text-sm text-zinc-700 leading-relaxed">
                            Bookmarks are private by default. You choose which collections or items appear on your public profile at <code>brainsync.pro/yourname</code>. We do not sell user data and we never train AI models on your personal collection.
                        </p>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="px-6 pb-20 max-w-3xl mx-auto">
                <div className="rounded-3xl bg-gradient-to-br from-emerald-700 via-emerald-950 to-zinc-950 p-8 sm:p-10 text-white relative overflow-hidden">
                    <Share2 className="absolute -right-4 -top-4 w-32 h-32 text-emerald-400/10" />
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Start your second brain</h3>
                    <p className="mt-3 text-sm sm:text-base text-emerald-100/85 max-w-md">
                        Free forever. 20 AI bookmarks per month included. No credit card required.
                    </p>
                    <Link to="/" className="mt-6 inline-block">
                        <Button size="lg" className="rounded-full bg-white text-emerald-900 hover:bg-emerald-50 gap-2 shadow-lg" data-testid="how-it-works-bottom-cta">
                            Get started <ArrowRight className="w-4 h-4" />
                        </Button>
                    </Link>
                </div>
            </section>
        </main>
    );
}
